import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { useDebounce } from 'react-use';

import type { CrossChainQuote } from '@/lib/api/0x-cross-chain/types';

import { makePairKey } from '@/components/TransactionModule/LimitMode/useLimitOrderRate';
import { getTokenAmount } from '@/components/TransactionModule/MarketMode/utils';
import {
  getPriceInputsForUsdPriceFetch,
  getTokenPrices,
} from '@/components/TransactionModule/utils';

import { CHAIN_IDS } from '@/constants/chain';

import { useTokenResult } from '@/hooks/tokenQueries/useTokenResult';
import { useCheckoutMode } from '@/hooks/useCheckoutMode';
import { useIntentsAvailability } from '@/hooks/useIntentsAvailability';
import type { SwapArgs } from '@/hooks/useSwap';
import { useUSDPrices } from '@/hooks/useUSDPrices';

import {
  pastedReceivingWalletAddressSelector,
  useMarketTradeSettingsStore,
} from '@/store/tradeSettings/market';

import { type ChainAddress } from '@/utils/models';

import { taggedLimitRateSelector, useLimitRateStore } from './limitRateStore';
import {
  buyInputSelector,
  checkoutSelector,
  sellInputSelector,
  useTradeModuleStore,
} from './store';
import {
  deriveAdvancedTradeQuote,
  type TokenUsdPrices,
  type TradeQuote,
  type TradeQuoteSource,
} from './tradeQuote';
import { useCrossChainTradeQuote } from './useCrossChainTradeQuote';
import { useMarketTradeQuote } from './useMarketTradeQuote';

export type TradeEngine = {
  quote: TradeQuote;
  tokenUsdPrices: TokenUsdPrices;
  tokenUsdPriceAvailability: {
    sellToken: boolean;
    buyToken: boolean;
  };
  /** True once the USD price query has answered, even when it holds no prices. */
  hasUsdPriceResponse: boolean;
  /** One-shot native-gas probe result for cross-chain trades. */
  gasProbeQuote: CrossChainQuote | undefined;
};

export type TradeEngineProviderProps = {
  sellTokenChainId: number | undefined;
  sellTokenAddress: ChainAddress | undefined;
  buyTokenChainId: number | undefined;
  buyTokenAddress: ChainAddress | undefined;
  children: ReactNode;
};

const TradeEngineContext = createContext<TradeEngine | undefined>(undefined);

/**
 * The engine facade: resolves which lane is authoritative, owns the shared
 * inputs (selection, debounce, USD prices), and selects that lane's normalized
 * quote. Lane-specific fetching and derivation live in the providers
 * (`useMarketTradeQuote`, `useCrossChainTradeQuote`, `deriveAdvancedTradeQuote`).
 */
function useTradeEngineValue({
  sellTokenChainId,
  sellTokenAddress,
  buyTokenChainId,
  buyTokenAddress,
}: Omit<TradeEngineProviderProps, 'children'>): TradeEngine {
  const sellInput = useTradeModuleStore(sellInputSelector);
  const buyInput = useTradeModuleStore(buyInputSelector);
  const checkout = useTradeModuleStore(checkoutSelector);
  const taggedLimitRate = useLimitRateStore(taggedLimitRateSelector);
  const { pastedReceivingWalletAddress } = useMarketTradeSettingsStore(
    pastedReceivingWalletAddressSelector,
  );
  const [checkoutMode] = useCheckoutMode();
  const { token: sellToken } = useTokenResult(sellTokenAddress, sellTokenChainId);
  const { token: buyToken } = useTokenResult(buyTokenAddress, buyTokenChainId);

  const sellInfo = useMemo(
    () =>
      sellTokenChainId != null && sellTokenAddress
        ? { address: sellTokenAddress, chainId: sellTokenChainId }
        : undefined,
    [sellTokenAddress, sellTokenChainId],
  );
  const buyInfo = useMemo(
    () =>
      buyTokenChainId != null && buyTokenAddress
        ? { address: buyTokenAddress, chainId: buyTokenChainId }
        : undefined,
    [buyTokenAddress, buyTokenChainId],
  );
  const sellAmount = useMemo(
    () => getTokenAmount(sellInput.decimal, sellToken?.decimals),
    [sellInput.decimal, sellToken?.decimals],
  );
  const queryArgs = useMemo<SwapArgs | null>(
    () =>
      sellToken && buyToken && sellAmount
        ? {
            buyTokenAddress: buyToken.address,
            buyTokenChainId: buyToken.chainId,
            sellTokenAddress: sellToken.address,
            sellTokenChainId: sellToken.chainId,
            sellAmount,
          }
        : null,
    [buyToken, sellAmount, sellToken],
  );
  const [debouncedQueryArgs, setDebouncedQueryArgs] = useState(queryArgs);
  useDebounce(() => setDebouncedQueryArgs(queryArgs), 300, [queryArgs]);

  const chainId = sellToken?.chainId || buyToken?.chainId || CHAIN_IDS.MAINNET;
  const isIntentsEnabled = useIntentsAvailability({ buyToken, chainId, sellToken }).isEnabled;
  const marketLane: 'swap' | 'intents' = isIntentsEnabled ? 'intents' : 'swap';
  const source: TradeQuoteSource =
    checkoutMode === 'advanced'
      ? 'advanced'
      : sellTokenChainId != null && buyTokenChainId != null && sellTokenChainId !== buyTokenChainId
        ? 'cross-chain'
        : marketLane;

  const usdPriceInputs = useMemo(
    () => getPriceInputsForUsdPriceFetch(sellInfo, buyInfo, sellToken?.issuer, buyToken?.issuer),
    [buyInfo, buyToken?.issuer, sellInfo, sellToken?.issuer],
  );
  const { data: usdPriceData } = useUSDPrices(usdPriceInputs);
  const { tokenUsdPrices, tokenUsdPriceAvailability } = useMemo(() => {
    const { sellTokenPrice, buyTokenPrice, nativeTokenPrice } = getTokenPrices(
      usdPriceData ?? [],
      sellInfo,
      buyInfo,
    );
    return {
      tokenUsdPrices: {
        sellTokenUsd: sellTokenPrice?.priceUsd ?? 0,
        buyTokenUsd: buyTokenPrice?.priceUsd ?? 0,
        nativeTokenUsd: nativeTokenPrice?.priceUsd ?? 0,
      },
      tokenUsdPriceAvailability: {
        sellToken: sellTokenPrice !== undefined,
        buyToken: buyTokenPrice !== undefined,
      },
    };
  }, [buyInfo, sellInfo, usdPriceData]);

  const pairKey = makePairKey(sellInfo, buyInfo);
  const hasTradeInputs = Boolean(sellToken && buyToken && sellAmount && BigInt(sellAmount) > 0n);

  const market = useMarketTradeQuote({
    lane: marketLane,
    enabled: source === marketLane,
    hasTradeInputs,
    queryArgs,
    debouncedQueryArgs,
    pairKey,
    chainId,
    isGasless: isIntentsEnabled,
    buyAmountDecimal: buyInput.decimal,
    buyAmountUsd: buyInput.usd,
    pausePolling: checkout.step === 'pending',
    sellToken,
    buyToken,
    tokenUsdPrices,
  });
  const crossChain = useCrossChainTradeQuote({
    enabled: source === 'cross-chain',
    hasTradeInputs,
    sellToken,
    buyToken,
    sellAmount,
    debouncedSellAmount: debouncedQueryArgs?.sellAmount,
    sellInputUsd: sellInput.usd,
    tokenUsdPrices,
    recipientAddress: pastedReceivingWalletAddress,
  });
  const advanced = useMemo(
    () =>
      deriveAdvancedTradeQuote({
        active: hasTradeInputs,
        taggedLimitRate,
        pairKey,
        sellInputDecimal: sellInput.decimal,
        buyToken,
      }),
    [buyToken, hasTradeInputs, pairKey, sellInput.decimal, taggedLimitRate],
  );

  const quote: TradeQuote =
    source === 'advanced' ? advanced : source === 'cross-chain' ? crossChain.quote : market;

  return useMemo(
    () => ({
      quote,
      tokenUsdPrices,
      tokenUsdPriceAvailability,
      hasUsdPriceResponse: usdPriceData !== undefined,
      gasProbeQuote: crossChain.gasProbeQuote,
    }),
    [crossChain.gasProbeQuote, quote, tokenUsdPriceAvailability, tokenUsdPrices, usdPriceData],
  );
}

/** Shares one page-owned trade engine with the module and sibling readers. */
export function TradeEngineProvider({
  children,
  ...props
}: TradeEngineProviderProps): ReactElement {
  const value = useTradeEngineValue(props);
  return <TradeEngineContext.Provider value={value}>{children}</TradeEngineContext.Provider>;
}

/** Reads the nearest trade engine instance. */
export function useTradeEngine(): TradeEngine {
  const value = useContext(TradeEngineContext);
  if (!value) throw new Error('useTradeEngine must be used within TradeEngineProvider.');
  return value;
}
